/**
 * Composer keyboard helpers.
 *
 * Enter sends, Shift+Enter inserts a newline — BUT never while an IME is
 * composing. CJK (and other) input methods fire a `keydown` with `key === 'Enter'`
 * AND `isComposing === true` when the user presses Enter to commit a candidate
 * (e.g. selecting the Hanzi/Kana for a pinyin/romaji buffer). If we treat that as
 * "send", we ship the half-composed phonetic buffer and clear the box — every IME
 * commit becomes an accidental send. Checking `isComposing` lets the IME consume
 * that Enter.
 */

/** Minimal structural shape of a React (or native) keydown event. */
export interface EnterKeyState {
  key: string;
  shiftKey: boolean;
  /** React `KeyboardEvent` exposes the composing flag on the native event. */
  nativeEvent?: { isComposing?: boolean };
  /** Some synthetic/native events expose it directly. */
  isComposing?: boolean;
}

/**
 * Should this keydown submit the composer? True only for a plain Enter that is
 * NOT part of an active IME composition and NOT combined with Shift.
 */
export function shouldSubmitOnEnter(e: EnterKeyState): boolean {
  if (e.key !== 'Enter' || e.shiftKey) return false;
  if (e.nativeEvent?.isComposing || e.isComposing) return false;
  return true;
}
